import { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { Alert, Box } from '@mui/material';
import { Button, Confirm, FormTab, FormTabProps, useGetList, useNotify, useRecordContext } from 'react-admin';

import client from 'src/data/api';
import { usePermissions } from 'src/hooks';
import { CustomerDataPackageRecord } from 'src/types';
import UnauthorizedFormTabContent from 'src/components/UnauthorizedFormTabContent';
import { CraftPageSection } from 'src/components/CraftPageSection';
import SubscriptionsList from './SubscriptionsList';

const CompaniesTab = ({ label = 'Companies', ...rest }: Omit<FormTabProps, 'label'> & { label?: string }) => {
  const dataPkgRecord = useRecordContext<CustomerDataPackageRecord>();
  const { permissions } = usePermissions();
  const notify = useNotify();

  const [disableAllCompaniesDialogOpen, setDisableAllCompaniesDialogOpen] = useState<boolean>(false);

  // fetch any subscriptions for "all companies" under the current data package
  const {
    data: subForAllCompaniesData,
    error: subForAllCompaniesError,
    isLoading: subForAllCompaniesLoading,
    refetch,
  } = useGetList('subscriptions', { filter: { customer_data_package_id: dataPkgRecord.id, all_companies: true } });

  const allCompaniesEnabled = !!subForAllCompaniesData?.length;

  useEffect(() => {
    if (!subForAllCompaniesError) return;
    notify('There was a problem fetching subscription data. Please refresh the page to try again.', {
      type: 'error',
    });
  }, [subForAllCompaniesError]);

  const [deleteAllCompaniesSub, { error: deleteAllCompaniesSubError, loading: deleteAllCompaniesSubLoading }] =
    useMutation(
      gql`
        mutation delete_subscriptions($customerDataPackageID: Int!) {
          delete_subscriptions(
            where: { customer_data_package_id: { _eq: $customerDataPackageID }, all_companies: { _eq: true } }
          ) {
            affected_rows
          }
        }
      `,
      { client, errorPolicy: 'all' },
    );

  useEffect(() => {
    if (!deleteAllCompaniesSubError) return;
    notify('There was a problem disabling subscription to all companies. Please try again.', {
      type: 'error',
    });
  }, [deleteAllCompaniesSubError]);

  const handleDisableAllCompaniesConfirm = async () => {
    await deleteAllCompaniesSub({ variables: { customerDataPackageID: dataPkgRecord.id } });
    refetch();
    setDisableAllCompaniesDialogOpen(false);
  };

  if (!permissions.has('subscription:read')) {
    return (
      <FormTab label={label} {...rest}>
        <UnauthorizedFormTabContent />
      </FormTab>
    );
  }

  return (
    <FormTab label={label} {...rest}>
      <CraftPageSection title="Company Subscriptions" loading={subForAllCompaniesLoading}>
        {allCompaniesEnabled && (
          <>
            <Alert severity="info" sx={{ mt: 1 }}>
              This data package is enabled for all companies.
            </Alert>
            <Alert severity="info" sx={{ mt: 1 }}>
              Click &quot;enable configuration&quot; below to configure company subscriptions.
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <Button
                label="Enable configuration"
                variant="contained"
                onClick={() => setDisableAllCompaniesDialogOpen(true)}
              />
            </Box>
          </>
        )}

        {!allCompaniesEnabled && <SubscriptionsList />}
      </CraftPageSection>

      <Confirm
        title="Enable company subscription configuration"
        content="Are you sure you want to disable automatic subscription to all companies for this data package?"
        onConfirm={handleDisableAllCompaniesConfirm}
        isOpen={disableAllCompaniesDialogOpen}
        loading={deleteAllCompaniesSubLoading}
        onClose={() => setDisableAllCompaniesDialogOpen(false)}
      />
    </FormTab>
  );
};

export default CompaniesTab;
